import { PersistentEventTypes } from '@tencent/slide-sdk-model';
import { MonitorStatus, type ILogger } from '@tencent/slide-sdk-model/src/manager/logger.interface';
import once from 'lodash/once';
import { HAS_OFFLINE_DATA, DEBUG_TAG, DELAY_RELEASE_LOCK_INTERVAL } from './constants';
import { MutationsPacksStorage } from './storage';
import { VisibilityScheduler } from './utils/visibility-scheduler';
import { MutexLockHelper } from './mutex-lock-helper';
import type { IMutexLockHelper } from './types/mutex-lock-helper.interface';
import type { IMutationsPack } from '@tencent/slide-sdk-model/src/manager/collaboration-manager';
import type { IEventManager } from '@tencent/slide-sdk-model/src/manager/event-manager';

// IMutationPack 的扩展，用于持久化存储
export type IMutationsPackExtended = IMutationsPack & {
    sendedCount: number;
    reason?: number;
};

// 事件参数,用于外部传参时进行类型限定
export interface IPersistentEventParam {
    mutationsPack: IMutationsPack;
    reason?: number;
}

export interface IPersistentConfig {
    globalPadId: string;
    localPadId: string;
    userId: string;
    editable: boolean;
    revision: number;
}

export type IOfflineEditServiceProvider = {
    getOfflineEditService: () => Promise<{
        getRecoverData: () => Promise<IMutationsPackExtended[]>;
        startRecovery: (recoveryCallback: () => Promise<void>) => Promise<void>;
    }>;
};

export enum RecoveryStatus {
    None = 'none',
    // 在读取数据库之前准备恢复数据
    BeforeGetRecoverData = 'beforeGetRecoverData',
    // 成功读取数据库之后准备恢复数据
    WillRecoverData = 'willRecoverData',
    StartRecoverData = 'startRecoverData',
    DidRecoverData = 'didRecoverData',
}

/**
 * changeset 持久化管理
 */
export class Persistent {
    // 是否有离线数据
    public hasOfflineData = false;
    // 是否支持离线编辑
    public supportOfflineEdit = true;
    public mutexLockHelper: IMutexLockHelper;

    private recoveryStatus = RecoveryStatus.None;

    // 初始化标志
    private dBInitialized = false;

    // 内存中的 mutationsPacks
    private mutationsPacks: IMutationsPackExtended[];

    private retryCount = 0;

    private storage?: MutationsPacksStorage;

    private reportError: (stage: string, message: string) => void;

    private visibilityScheduler = new VisibilityScheduler();
    private releaseLockTimer: NodeJS.Timer;
    private config: IPersistentConfig;

    constructor(
        private event: IEventManager,
        private logger: ILogger,
        private offlineEditServiceProvider?: IOfflineEditServiceProvider,
    ) {
        this.reportError = once((stage, message: string) => {
            this.logger.tdw?.({
                opername: 'slide-base-sdk',
                module: 'persistent',
                action: 'db-error',
            });

            if (typeof window.log?.report === 'function') {
                window.log.report('SLIDE_V3_DB_ERROR_LOG');
            }
            this.logger.monitor?.reportStatus?.('db-error', MonitorStatus.ERROR, message, [stage]);
        });
        this.mutationsPacks = [];
        this.logger.info(DEBUG_TAG, 'init');
    }

    public async init(config: IPersistentConfig) {
        this.config = config;
        this.hasOfflineData = this.getHasOfflineDataFlag() === 'true';
        if (this.hasOfflineData) this.willRecoverPersistentData();
        // this.beforeWillRecoverPersistentData();
        this.initMutexLockHelper(`D${btoa(config.localPadId)}`);
        this.offlineEditServiceProvider
            ? await this.initFromNativeStorage(config)
            : await this.initFromWebStorage(config);
        this.dBInitialized = true;
        return this.hasOfflineData;
    }

    /**
     * 页面关闭业务处理
     */
    public onbeforeUnload = () => {
        this.mutexLockHelper.dispose();
        if (!this.mutationsPacks.length && this.dBInitialized === true) {
            this.storage?.delete().catch(() => {
                // Ignore
            });
            return;
        }
    };

    /**
     * 立即存储内存中的持久化数据到数据库
     */
    public async saveCacheImmediately(): Promise<boolean> {
        this.updateHasOfflineDataFlag(!!this.mutationsPacks.length);
        this.mutexLockHelper.lock();
        this.delayReleaseLock();
        if (this.offlineEditServiceProvider) return true;
        try {
            await this.storage?.upsert(this.mutationsPacks);
            this.resetRetryCount();
        } catch (e) {
            const shouldRetry = this.handleDatabaseError(e as Error, 'save');
            if (shouldRetry) {
                // 保存失败时，说明可能遇到 Safari 页面隐藏时 DB 不可用的 Bug：https://bugs.webkit.org/show_bug.cgi?id=202705
                // 此时判断页面是否隐藏，如果隐藏则等待页面显示后再次尝试保存
                return this.visibilityScheduler.schedule(() => this.saveCacheImmediately());
            }
            this.logger.error(DEBUG_TAG, 'saveCacheImmediately error: ', e?.toString());
            return false;
        }
        return true;
    }

    /**
     * 添加 mutationsPack 或更新 mutationsPack
     */
    public additionOrUpdate = (mutationsPack: IMutationsPack) => {
        if (!mutationsPack) {
            this.logger.error(DEBUG_TAG, 'additionOrUpdateSeq mutationsPack is invalid');
            return;
        }
        const mutationsPackExtended = this.createMutationsPackExtended(mutationsPack);
        this.handleAdditionOrUpdate(mutationsPackExtended);

        this.waitForDBInitialized().then(() => {
            this.saveCacheImmediately();
            this.event.emit(PersistentEventTypes.AddOrUpdateData, [mutationsPackExtended]);
        });
    };

    // public additionOrUpdateMultiple = (mutationsPacks: IMutationsPack[]) => {
    //     if (!mutationsPacks.length) {
    //         this.logger.error(DEBUG_TAG, 'additionOrUpdateSeq persistentItems is empty');
    //         return;
    //     }
    //     const mutationsPacksExtended = mutationsPacks.map((mutationsPack) => {
    //         const mutationsPackExtended = this.createMutationsPackExtended(mutationsPack);
    //         this.handleAdditionOrUpdate(mutationsPackExtended);
    //         return mutationsPackExtended;
    //     });
    //     this.saveCacheImmediately();
    //     this.event.emit(PersistentEventTypes.AddOrUpdateData, mutationsPacksExtended);
    // };

    public replaceAllData = async (mutationsPacks: IMutationsPack[]) => {
        this.logger.info(DEBUG_TAG, 'replaceAllData is ', mutationsPacks);
        this.mutationsPacks = mutationsPacks.map((mutationsPack) => {
            return this.createMutationsPackExtended(mutationsPack);
        });
        const modifiedMutationsPack = this.mutationsPacks.slice();
        await this.waitForDBInitialized();
        const res = await this.saveCacheImmediately();
        this.event.emit(PersistentEventTypes.ReplaceAllData, modifiedMutationsPack);
        return res;
    };

    /**
     * 删除单个 taskId 对应的持久化数据
     */
    public deletionSingle = (taskId: number) => {
        if (!taskId) {
            this.logger.error(DEBUG_TAG, 'deletionSingle param: mutationsPack is null');
            return;
        }
        this.handleDeletion([taskId]);

        this.waitForDBInitialized().then(() => {
            this.saveCacheImmediately();
            this.event.emit(PersistentEventTypes.RemoveData, [taskId]);
        });
    };

    /**
     * 删除多个 taskId 对应的持久化数据
     */
    public deletionMultiple = (taskIds: number[]) => {
        this.handleDeletion(taskIds);
        this.waitForDBInitialized().then(() => {
            this.saveCacheImmediately();
            this.event.emit(PersistentEventTypes.RemoveData, taskIds);
        });
    };

    public modifyErrorInfo = (taskId: number, code: number) => {
        if (!taskId || code === undefined) {
            this.logger.info(`${DEBUG_TAG} modify param is invalid`);
            return;
        }
        const mutationPacks = this.mutationsPacks.find((item) => {
            if (item.taskId === taskId) {
                Reflect.set(item, 'reason', code);
                Reflect.set(item, 'sendedCount', (item.sendedCount ?? 0) + 1);
                return true;
            }
            return false;
        });
        mutationPacks && this.event.emit(PersistentEventTypes.AddOrUpdateData, [mutationPacks]);
    };

    public async deleteAllData() {
        const removedData = this.mutationsPacks.splice(0, this.mutationsPacks.length);
        return this.waitForDBInitialized().then(() => {
            this.saveCacheImmediately();
            this.event.emit(
                PersistentEventTypes.RemoveData,
                removedData.map(({ taskId }) => taskId),
            );
        });
    }

    /**
     * 校验并分发 mutationsPacks
     * native 离线恢复也会走这个逻辑
     * @param mutationsPacks
     */
    public startToRecovery = (): Promise<void> => {
        const recoveryCallback = async () => {
            if (!this.mutationsPacks.length) {
                this.updateHasOfflineDataFlag(false);
                this.didRecoverPersistentData();
                return;
            }
            this.startRecoverPersistentData();
            this.logger.info(`${DEBUG_TAG} all mutationsPacks length`, this.mutationsPacks.length, this.mutationsPacks);
            // 确保离线数据恢复完成之后再开启socket
            return new Promise<void>((resolve) => {
                this.event.emit(
                    PersistentEventTypes.GetAllMutationsPacks,
                    { mutationsPacks: this.mutationsPacks },
                    () => {
                        this.updateHasOfflineDataFlag(false);
                        this.didRecoverPersistentData();
                        resolve();
                    },
                );
            });
        };
        if (this.offlineEditServiceProvider) {
            return this.offlineEditServiceProvider
                .getOfflineEditService()
                .then((service) => service.startRecovery(recoveryCallback));
        }
        return recoveryCallback();
    };

    public getMutationsPacks() {
        return this.mutationsPacks;
    }

    public setMutationsPacks(mutationsPacks: IMutationsPackExtended[]) {
        this.mutationsPacks = mutationsPacks;
    }

    public getRecoveryStatus() {
        return this.recoveryStatus;
    }

    // public onBeforeGetRecoveryData(cb: () => void) {
    //     if (this.recoveryStatus === RecoveryStatus.BeforeGetRecoverData) {
    //         cb();
    //         return;
    //     }
    //     const off = this.event.on(PersistentEventTypes.RecoveryStatusChange, (recoveryStatus) => {
    //         if (recoveryStatus === RecoveryStatus.BeforeGetRecoverData) {
    //             cb();
    //             off();
    //         }
    //     });
    // }

    /**
     * 需要恢复离线数据
     * @param cb
     * @returns
     */
    public onNeedRecoveryData(cb: () => void) {
        if ([RecoveryStatus.WillRecoverData, RecoveryStatus.StartRecoverData].includes(this.recoveryStatus)) {
            cb();
            return;
        }
        const off = this.event.on(PersistentEventTypes.RecoveryStatusChange, (recoveryStatus) => {
            if ([RecoveryStatus.WillRecoverData, RecoveryStatus.StartRecoverData].includes(recoveryStatus)) {
                cb();
                off();
            }
        });
    }

    /**
     * 已经恢复离线数据
     * @param cb
     * @returns
     */
    public onDidRecoveryData(cb: () => void) {
        if (this.recoveryStatus === RecoveryStatus.DidRecoverData) {
            cb();
            return;
        }
        const off = this.event.on(PersistentEventTypes.RecoveryStatusChange, (recoveryStatus) => {
            if (recoveryStatus === RecoveryStatus.DidRecoverData) {
                cb();
                off();
            }
        });
    }

    /**
     * 恢复数据完成
     */
    public didRecoverPersistentData = () => {
        this.recoveryStatus = RecoveryStatus.DidRecoverData;
        this.event.emit(PersistentEventTypes.RecoveryStatusChange, RecoveryStatus.DidRecoverData);
    };

    /**
     * 正常业务不会使用，用作导出用户现场用
     */
    public exportDBData = async () => {
        const tableData = await this.storage?.entry();
        return tableData?.map(([id, model]) => ({
            id,
            mutationsPacks: model.getMutationsPacks(),
        }));
    };

    /**
     * 正常业务不会使用，用作恢复用户现场用
     */
    public importDBData = async (data: Array<{ id: string; mutationsPacks: IMutationsPackExtended[] }>) => {
        return this.storage?.bulkUpsert(data);
    };

    /**
     * 数据库错误统计
     * 返回是否应该重试
     * @returns
     */
    public handleDatabaseError = (e: Error, stage: 'get' | 'save' | 'init', isFatalError?: boolean) => {
        if (!isFatalError && this.retryCount < 3) {
            this.retryCount += 1;
            return true;
        }

        const message = `PersistentError: [${stage}] ${e?.toString()}`;
        this.supportOfflineEdit = false;
        if (stage === 'init') {
            this.event.emit(PersistentEventTypes.OpenDataBaseError);
        } else {
            this.event.emit(PersistentEventTypes.DataBaseFatalError, !!this.mutationsPacks.length);
        }
        this.logger.error(DEBUG_TAG, message);
        this.reportError(stage, message);
        return false;
    };

    private initFromNativeStorage = async (config: IPersistentConfig) => {
        if (!config.editable) {
            this.deleteAllData();
            return;
        }
        const offlineEditService = await this.offlineEditServiceProvider?.getOfflineEditService();
        if (offlineEditService?.getRecoverData) {
            const recoverData = await offlineEditService.getRecoverData();
            if (recoverData.length) {
                this.hasOfflineData = true;
                this.willRecoverPersistentData();
            }
            return recoverData;
        }
    };

    private initFromWebStorage = async (config: IPersistentConfig) => {
        // 由 globalPadId 和 userId 组成的唯一标识，作为表中的key
        const uniqueId = `${config.globalPadId}_${config.userId}`;
        try {
            this.storage = new MutationsPacksStorage(this.logger, uniqueId, DEBUG_TAG);
            await this.storage?.open().catch((err) => {
                // 打开失败时，说明可能遇到 Safari 页面隐藏时 DB 不可用的 Bug：https://bugs.webkit.org/show_bug.cgi?id=202705
                // 此时判断页面是否隐藏，如果隐藏则等待页面显示后再次尝试打开
                this.logger.error(DEBUG_TAG, 'open error: ', err.toString());
                this.visibilityScheduler
                    .schedule(() => this.storage?.open())
                    .catch((err) => {
                        this.handleDatabaseError(err, 'init', true);
                    });
            });
        } catch (e: any) {
            this.logger.error(DEBUG_TAG, 'init error: ', e.toString());
            this.handleDatabaseError(e, 'init', true);
            return;
        }
        if (config.editable) {
            const recoverData = await this.getRecoverData();
            if (recoverData.length) {
                this.hasOfflineData = true;
                this.willRecoverPersistentData();
            }
            return recoverData;
        }
        this.storage?.delete().catch(() => {
            // Ignore
        });
    };

    /**
     * 添加或更新数据
     * @param modifiedMutationsPack
     */
    private handleAdditionOrUpdate = (modifiedMutationsPack: IMutationsPackExtended) => {
        const mutationsPack = this.mutationsPacks.find((item) => item.taskId === modifiedMutationsPack.taskId);
        if (mutationsPack) {
            Reflect.set(mutationsPack, 'mutations', modifiedMutationsPack.mutations);
            this.logger.info(`${DEBUG_TAG} update mutationsPack, taskId:`, mutationsPack.taskId);
            return;
        }

        this.mutationsPacks.push(modifiedMutationsPack);
        this.logger.info(`${DEBUG_TAG} add mutationsPack, taskId:`, modifiedMutationsPack.taskId);
    };

    /**
     * 处理删除业务
     * @param taskIds
     */
    private handleDeletion = (taskIds: number[]) => {
        taskIds.forEach((taskId) => {
            const index = this.mutationsPacks.findIndex(({ taskId: taskIdFromMutationsPack, revision }) => {
                if (taskId === taskIdFromMutationsPack) {
                    this.logger.info(`${DEBUG_TAG} handleDeletion:`, {
                        revision,
                        taskId: taskIdFromMutationsPack,
                    });
                    return true;
                }
                return false;
            });
            index !== -1 && this.mutationsPacks.splice(index, 1);
        });
    };

    // private beforeWillRecoverPersistentData = () => {
    //     this.recoveryStatus = RecoveryStatus.BeforeGetRecoverData;
    //     this.event.emit(PersistentEventTypes.RecoveryStatusChange, RecoveryStatus.BeforeGetRecoverData);
    // };

    /**
     * 准备恢复数据
     */
    private willRecoverPersistentData = () => {
        this.recoveryStatus = RecoveryStatus.WillRecoverData;
        this.event.emit(PersistentEventTypes.RecoveryStatusChange, RecoveryStatus.WillRecoverData);
    };

    /**
     * 开始恢复数据
     */
    private startRecoverPersistentData = () => {
        this.recoveryStatus = RecoveryStatus.StartRecoverData;
        this.event.emit(PersistentEventTypes.RecoveryStatusChange, RecoveryStatus.StartRecoverData);
    };

    /**
     * 获取全量数据
     */
    private async getRecoverData(): Promise<IMutationsPackExtended[]> {
        try {
            this.mutationsPacks = (await this.storage?.get()) ?? [];
            this.resetRetryCount();
        } catch (e) {
            const shouldRetry = this.handleDatabaseError(e as Error, 'get');
            if (shouldRetry) {
                // 获取失败时，说明可能遇到 Safari 页面隐藏时 DB 不可用的 Bug：https://bugs.webkit.org/show_bug.cgi?id=202705
                // 此时判断页面是否隐藏，如果隐藏则等待页面显示后再次尝试获取
                return this.visibilityScheduler.schedule(() => this.getRecoverData());
            }
            this.logger.error(DEBUG_TAG, 'getDataAndTryToRecover error', e?.toString());
            this.didRecoverPersistentData();
            return [];
        }
        return this.mutationsPacks;
    }

    /**
     * 创建 MutationsPackExtended 对象用于存储
     * @param mutationPack
     */
    private createMutationsPackExtended = (mutationPack: IMutationsPack): IMutationsPackExtended => {
        return {
            ...mutationPack,
            sendedCount: 0,
        };
    };

    private resetRetryCount() {
        this.retryCount = 0;
    }

    /**
     * 锁延迟释放定时器处理
     *
     * @private
     * @return {*}  {void}
     */
    private delayReleaseLock(): void {
        this.resetReleaseLockTimer();
        // 没有持有锁就不需要定期检查及释放了
        if (!this.mutexLockHelper.isLocked()) {
            return;
        }

        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        this.releaseLockTimer = setInterval(async () => {
            if (!!this.mutationsPacks.length) {
                return;
            }
            await this.mutexLockHelper.release();
            this.resetReleaseLockTimer();
        }, DELAY_RELEASE_LOCK_INTERVAL);
    }

    private resetReleaseLockTimer() {
        if (this.releaseLockTimer) {
            clearInterval(this.releaseLockTimer);
        }
    }

    private async initMutexLockHelper(secretId: string) {
        this.mutexLockHelper = new MutexLockHelper(secretId, this.logger);
        const isLockSuccess = await this.mutexLockHelper.lock();
        this.logger.info(DEBUG_TAG, `init finish. isLockSuccess:${isLockSuccess}.`);
        this.delayReleaseLock();
    }

    private updateHasOfflineDataFlag(hasOfflineData: boolean) {
        if (this.hasOfflineData === hasOfflineData) return;
        this.hasOfflineData = hasOfflineData;
        localStorage.setItem(
            `${this.config.globalPadId}-${this.config.userId}-${HAS_OFFLINE_DATA}`,
            String(hasOfflineData),
        );
    }

    private getHasOfflineDataFlag() {
        return localStorage.getItem(`${this.config.globalPadId}-${this.config.userId}-${HAS_OFFLINE_DATA}`);
    }

    private async waitForDBInitialized() {
        if (this.dBInitialized) return;
        await new Promise<void>((resolve) => {
            const off = this.event.on(PersistentEventTypes.DataBaseInitialized, () => {
                off();
                resolve();
            });
        });
    }
}
